import { Header, useIntercomEditorActive } from '@studio/design-system/dialog';
import { AnimatePresence, motion, Motions } from '@studio/design-system/motion';
import type { HtmlAttributes } from '@studio/design-system/props';
import type { ComponentType, ReactElement } from 'react';
import { createElement } from 'react';
import { twMerge } from 'cn';
import { Panel as SettingsPanel } from './settings/index.js';
import { Panel as SourcePanel } from './source/index.js';
import { useStore } from './store/hooks.js';
import { Panel } from './store/index.js';
import { useVirtualPageview } from '#~/analytics/pageview/index.js';
import { WORKFLOW_MAPPINGS } from '#~/analytics/pageview/constants.js';
import type { WorkflowMappingKey } from '#~/analytics/pageview/types.js';

const PanelComponents: Record<Panel, ComponentType<HtmlAttributes>> = {
  [Panel.Source]: SourcePanel,
  [Panel.Settings]: SettingsPanel
};

/** Add audio `<Scaffold>` component. */
export const Scaffold = ({ className, ...props }: HtmlAttributes): ReactElement => {
  const panel = useStore((µ) => µ.panel);
  const flowId = useStore((µ) => µ.flowId);
  const isIntercomPreview = useIntercomEditorActive();

  // Panel → canonical virtual screen. useVirtualPageview owns the fire-once
  // rule and keys it on the canonical path, so a panel switch that resolves to
  // the same screen no longer emits a second $pageview.
  let mappingKey: WorkflowMappingKey | undefined;

  if (panel === Panel.Source) {
    mappingKey = 'audio-source-discover';
  } else if (panel === Panel.Settings) {
    mappingKey = 'audio-source-configure';
  }

  const mapping = mappingKey === undefined ? undefined : WORKFLOW_MAPPINGS[mappingKey];

  useVirtualPageview(mapping?.path ?? null, true, {
    ...(mapping === undefined ? {} : { category: mapping.category }),
    ...(mapping?.section === undefined ? {} : { section: mapping.section }),
    flow_id: flowId
  });

  return (
    <div
      className={twMerge(
        'flex flex-col',
        isIntercomPreview
          ? 'h-[min(800px,calc(100vh-120px))] w-[min(1100px,calc(100vw-140px))] overflow-auto'
          : 'max-h-[calc(100vh-40px)] w-[1100px] overflow-hidden',
        className
      )}
      {...props}
    >
      <Header title='Add a reference audio source' />

      <AnimatePresence>
        <motion.div className='flex grow' key={panel} {...Motions.Fade}>
          {createElement(PanelComponents[panel])}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
